import * as React from 'react'
import { withNamespaces } from 'react-i18next'
import { withRouter, useParams } from 'react-router-dom'
import { useState } from 'react'
import { connect } from 'react-redux'
import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper'
import {
    Row,
    Col,
    Container,
    Card,
    CardBody,
    CardTitle,
    Alert,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner
} from 'reactstrap'
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone
} from 'react-bootstrap-table2-paginator'
import BootstrapTable from 'react-bootstrap-table-next'
import overlayFactory from 'react-bootstrap-table2-overlay'
import { EMPTY_LIST } from 'src/common/constants'
import { toast } from 'react-toastify'
import Navigator from '../../../components/navigator'
import { useQuery, useMutation } from '@tanstack/react-query'
import DivisionManagementService from '../../../api/DivisionManagementService'
import EntityManagementService from '../../../api/EntityManagementService'
import InstituteManagementService from '../../../api/InstituteManagementService'
import CreateAndEditModal from './components/CreateAndEditModal'
import { DivisionSizes } from '../../../constants/Common'

const Divisions = ({ t }) => {
    const { entityId, instituteId } = useParams()

    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25
    })

    const [showCreateDivisionModal, setShowCreateDivisionModal] = useState(false)
    const [selectedDivisionToEdit, setSelectedDivisionToEdit] = useState(null)

    const [selectedDivisionToDelete, setSelectedDivisionToDelete] =
        React.useState(null)

    const divisionsService = new DivisionManagementService()
    const entityService = new EntityManagementService()
    const instituteService = new InstituteManagementService()

    const tableColumns = [
        {
            dataField: 'title',
            text: t('Title'),
            sort: false,
            style: {
                width: '200px'
            },
            key: 1
        },
        {
            dataField: 'short_name',
            text: t('Short Name'),
            style: {
                width: '200px'
            },
            sort: false,
            key: 2
        },
        {
            dataField: 'accounting_code',
            text: t('Accounting Code'),
            sort: false,
            key: 3
        },
        {
            dataField: 'division_size',
            text: t('Department Size'),
            sort: false,
            key: 4,
            formatter: (cellContent) => {
                return `${DivisionSizes[cellContent]?.title} ${t("Employees")}`
            }
        },
        {
            dataField: 'actions',
            text: t('Action'),
            sort: false,
            key: 5,
            formatter: (_cellContent, row) => {
                return (
                    <div className='actions'>
                        <Button
                            onClick={() => {
                                setSelectedDivisionToEdit(row)
                            }}
                            outline
                            color='primary'
                            className='border-0'
                        >
                            <i className='ri-pencil-line font-size-24' />
                        </Button>
                        <Button
                            onClick={() => setSelectedDivisionToDelete(row.id)}
                            outline
                            color='danger'
                            className='border-0'
                        >
                            <i className='ri-delete-bin-line font-size-24' />
                        </Button>
                    </div>
                )
            }
        }
    ]

    const {
        isFetching: divisionsAreFetching,
        refetch: refetchDivisionsList,
        data: divisionsList
    } = useQuery({
        queryKey: [
            'entity-management-fetch-entity-divisions-query',
            tableFilters
        ],
        queryFn: async () => {
            if (instituteId) {
                return await divisionsService.fetchInstituteDivisionsList(instituteId, tableFilters)
            }

            return await divisionsService.fetchLegalDivisionsList(entityId, tableFilters)
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching departments list.'), {
                type: 'error'
            })
        }
    })

    const {
        isFetching: entityDetailsIsFetching,
        data: entityDetails
    } = useQuery({
        queryKey: [
            'entity-management-fetch-entity-details-query',
            entityId
        ],
        queryFn: async () => {
            return await entityService.show(entityId)
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching details of entity.'), {
                type: 'error'
            })
        }
    })

    const {
        isFetching: instituteDetailsIsFetching,
        data: instituteDetails
    } = useQuery({
        queryKey: [
            'entity-management-fetch-institute-details-query',
            instituteId
        ],
        queryFn: async () => {
            return await instituteService.show(instituteId)
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (_error) => {
            toast(t('An error occurred while fetching details of institute.'), {
                type: 'error'
            })
        }
    })

    const { isLoading: deleteDivisionIsLoading, mutate: mutateDivisionDelete } =
        useMutation({
            mutationFn: async (divisionId) => {
                return await divisionsService.delete(divisionId)
            },
            onSuccess: () => {
                setSelectedDivisionToDelete(null)
                refetchDivisionsList()

                toast(t('Department deleted successfully.'), {
                    type: 'success'
                })
            },
            onError: () => {
                toast(t('Failed to delete department.'), {
                    type: 'error'
                })
            }
        })

    const {
        isLoading: createDivisionIsLoading,
        mutate: mutateDivisionCreation
    } = useMutation({
            mutationFn: async (payload) => {
                return await divisionsService.create(payload)
            },
            onSuccess: () => {
                refetchDivisionsList()
                setShowCreateDivisionModal(false)

                toast(t('Department created successfully.'), {
                    type: 'success'
                })
            },
            onError: () => {
                toast(t('Failed to create department.'), {
                    type: 'error'
                })
            }
        })

    const { isLoading: editDivisionIsLoading, mutate: mutateDivisionEdit } =
        useMutation({
            mutationFn: async (payload) => {
                return await divisionsService.edit(selectedDivisionToEdit.id, payload)
            },
            onSuccess: () => {
                setSelectedDivisionToEdit(null)
                refetchDivisionsList()

                toast(t('Department updated successfully.'), {
                    type: 'success'
                })
            },
            onError: () => {
                toast(t('Failed to edit department.'), {
                    type: 'error'
                })
            }
        })

    const navigatorMemoBreads = React.useMemo(() => {
        return [
            {
                title: 'Entity Management',
                link: '/admin/entity-management/entities'
            },
            {
                title: 'Entities',
                link: `/admin/entity-management/entities`
            },
            {
                title: entityDetailsIsFetching ? '...' : entityDetails?.title,
                link: `/admin/entity-management/entities/${entityId}/detail`
            },
            {
                title: 'Institutes / Branches',
                link: `/admin/entity-management/entities/${entityId}/detail`
            },
            {
                title: instituteDetailsIsFetching ? '...' : instituteDetails?.title,
                link: `/admin/entity-management/entities/${entityId}/detail`
            },
            {
                title: 'Departments',
                link: '#'
            }
        ]
    }, [
        entityDetailsIsFetching, 
        instituteDetailsIsFetching, 
        entityDetails, 
        instituteDetails
    ])

    const NoDataIndication = () => {
        if (!divisionsAreFetching && !parseInt(divisionsList?.itemsCount)) {
            return (
                <div className='alert m-0' role='alert'>
                    <p className='mb-0 text-center'>
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <React.Fragment />
    }

    const handleTableChange = (type, data) => {
        switch (type) {
            case 'pagination': {
                const { page, sizePerPage } = data

                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage
                })
                break
            }
            default:
                return false
        }
    }

    const renderModals = () => {
        if (divisionsAreFetching) {
            return null
        }

        return (
            <React.Fragment>
                <Modal
                    size='md'
                    scrollable
                    isOpen={!!selectedDivisionToDelete}
                    backdrop='static'
                >
                    <ModalHeader
                        toggle={() => setSelectedDivisionToDelete(null)}
                    >
                        {t('Delete Department')}
                    </ModalHeader>

                    <ModalBody>
                        <p>
                            {t(
                                'Are you sure you want to delete this department?'
                            )}
                        </p>

                        <ModalFooter>
                            <Button
                                disabled={deleteDivisionIsLoading}
                                color='primary'
                                outline
                                className='waves-effect waves-light'
                                type='button'
                                onClick={() => {
                                    setSelectedDivisionToDelete(null)
                                }}
                            >
                                {t('Cancel')}
                            </Button>

                            <Button
                                disabled={deleteDivisionIsLoading}
                                color='danger'
                                type='button'
                                className='waves-effect waves-light'
                                onClick={() =>
                                    mutateDivisionDelete(
                                        selectedDivisionToDelete
                                    )
                                }
                            >
                                {deleteDivisionIsLoading && (
                                    <Spinner
                                        animation='border'
                                        variant='white'
                                        size='sm'
                                        className='me-1'
                                    />
                                )}
                                {t('Delete')}
                            </Button>
                        </ModalFooter>
                    </ModalBody>
                </Modal>

                <CreateAndEditModal
                    show={showCreateDivisionModal || selectedDivisionToEdit}
                    toggle={() => {
                        setShowCreateDivisionModal(false)
                        setSelectedDivisionToEdit(null)
                    }}
                    onSubmit={(values, action) => {
                        if (action === 'edit') {
                            mutateDivisionEdit({
                                ...values,
                                institute: instituteId
                            })
                        } else {
                            mutateDivisionCreation({
                                ...values,
                                institute: instituteId
                            })
                        }
                    }}
                    division={selectedDivisionToEdit}
                    submitInProgress={
                        createDivisionIsLoading || editDivisionIsLoading
                    }
                />
            </React.Fragment>
        )
    }

    if (!isFeatureEnabledForActiveModule('entity_management')) {
        return (
            <div className='page-content'>
                <Alert color='warning'>
                    {t('Please upgrade your plan to access this feature')}.
                </Alert>
            </div>
        )
    }

    return (
        <div className='page-content'>
            <Container fluid>
                <Row>
                    <Col lg='12' className='mb-4'>
                        <Navigator
                            backButtonOptions={(props) => {
                                return {
                                    ...props,
                                    enable: true
                                }
                            }}
                            breadCrumbs={navigatorMemoBreads}
                        />
                    </Col>

                    <Col sm='12'>
                        <Card>
                            <CardBody>
                                <CardTitle tag='h4'>
                                    {t('Manage departments of')}{' '}
                                    {instituteDetailsIsFetching ? '...' : instituteDetails?.title}
                                </CardTitle>
                                <PaginationProvider
                                    pagination={paginationFactory({
                                        custom: true,
                                        page: tableFilters.pageIndex,
                                        sizePerPage: tableFilters.pageSize,
                                        totalSize: divisionsList?.itemsCount || 0,
                                        withFirstAndLast: false,
                                        alwaysShowAllBtns: true,
                                        prePageText: (
                                            <span>
                                                <i className='ri-arrow-left-s-line' />{' '}
                                                {t('Back')}
                                            </span>
                                        ),
                                        nextPageText: (
                                            <span>
                                                {t('Next')}{' '}
                                                <i className='ri-arrow-right-s-line' />
                                            </span>
                                        ),
                                        prePageTitle: t('Pre page'),
                                        firstPageTitle: t('Next page'),
                                        showTotal: true,
                                        hideSizePerPage: false,
                                        sizePerPageList: [
                                            {
                                                text: '25',
                                                value: 25
                                            },
                                            {
                                                text: '50',
                                                value: 50
                                            }
                                        ]
                                    })}
                                >
                                    {({
                                        paginationProps,
                                        paginationTableProps
                                    }) => (
                                        <React.Fragment>
                                            <Row className='mb-5'>
                                                <Col
                                                    sm='12'
                                                    className='d-flex justify-content-end'
                                                >
                                                    <Button
                                                        onClick={() =>
                                                            setShowCreateDivisionModal(
                                                                true
                                                            )
                                                        }
                                                        color='primary'
                                                    >
                                                        {t('Add Department')}
                                                    </Button>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col sm='12'>
                                                    <BootstrapTable
                                                        remote={{
                                                            pagination: true,
                                                            filter: false,
                                                            sort: true,
                                                            cellEdit: false,
                                                            search: false
                                                        }}
                                                        loading={
                                                            divisionsAreFetching
                                                        }
                                                        overlay={overlayFactory(
                                                            {
                                                                spinner: (
                                                                    <Spinner
                                                                        animation='border'
                                                                        variant='primary'
                                                                        size='md'
                                                                    />
                                                                ),
                                                                text: t('Loading...')
                                                            }
                                                        )}
                                                        onTableChange={
                                                            handleTableChange
                                                        }
                                                        defaultSorted={[]}
                                                        keyField='id'
                                                        responsive
                                                        bordered={false}
                                                        data={
                                                            divisionsList?.divisions ||
                                                            []
                                                        }
                                                        striped
                                                        columns={
                                                            tableColumns
                                                        }
                                                        wrapperClasses='table-responsive'
                                                        classes='table tpdd-table'
                                                        headerWrapperClasses='thead-light'
                                                        style={{
                                                            overflowX:
                                                                'auto'
                                                        }}
                                                        noDataIndication={() => (
                                                            <NoDataIndication />
                                                        )}
                                                        {...paginationTableProps}
                                                    />
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col sm='12'>
                                                    <div className='tpdd-pagination-style-1 justify-content-end'>
                                                        <div 
                                                            className={`${parseInt(divisionsList?.itemsCount) < parseInt(divisionsList?.pageSize) ? 'd-none' : ''}`}
                                                        >
                                                            <PaginationListStandalone
                                                                {...paginationProps}
                                                            />
                                                        </div>

                                                        <SizePerPageDropdownStandalone
                                                            {...paginationProps}
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>
                                        </React.Fragment>
                                    )}
                                </PaginationProvider>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {renderModals()}
        </div>
    )
}

const mapToState = (state) => {
    const { Organization } = state

    return {
        Organization
    }
}

export default withNamespaces()(withRouter(connect(mapToState, {})(Divisions)))
